/**
 * 취향분석 (BETA) — 앱 환경설정 > 취향분석.
 *
 * tastee_website/src/pages/Quiz.tsx 에서 옮겨 왔다. 화면 뼈대와 문구는
 * 그대로고, 앱에 맞춰 셋을 바꿨다.
 *
 *   1. 웹사이트 머리말(Navigation)·꼬리말(Footer)을 뺐다. 앱 안에서 그걸
 *      그리면 사업자등록번호까지 나와 웹페이지로 보인다. 대신 AppBar 를 쓴다.
 *   2. 스토어 버튼을 뺐다. 이미 앱을 쓰는 사람에게 '앱 받기'는 말이 안 되고,
 *      누르면 앱 밖으로 튕긴다.
 *   3. 저장을 apiClient 로 한다. 같은 오리진이라 로그인 토큰이 함께 가고,
 *      서버가 결과에 user_id 를 적는다 — 웹사이트 쪽은 익명으로 남는다.
 *
 * 문항·점수 계산 파일은 웹사이트와 **두 벌**이다. 각 파일 머리말 참고.
 */
import React, { useEffect, useRef, useState } from "react";
import AppBar from "../components/AppBar";
import toastUtil from "../utils/toast";
import { cn } from "../utils/cn";
import { CHIP, QUESTIONS, TRAITS, TRAIT_LABEL, type Answer, type Trait } from "../data/quiz";
import { SECOND_LINE, SECOND_PARAGRAPH, TYPES, findCombo } from "../data/quizTypes";
import { score, type QuizResult, type Selections } from "../lib/quizScoring";
import quizApi from "../api/quiz";
import { useAuthStore } from "../stores/authStore";
import SubmitButton from "../components/SubmitButton";

const { logEvent, sendFeedback, submitResponse } = quizApi;

type Phase = "intro" | "playing" | "result" | "shared";

/** 14문항 + 마지막 칩 화면 */
const STEPS = QUESTIONS.length + 1;
const isTrait = (v: string | null): v is Trait => !!v && (TRAITS as readonly string[]).includes(v);

// 시작 화면 미리보기용 예시 값

// ---------------------------------------------------------------------------
// 공통 조각
// ---------------------------------------------------------------------------
const Pill = ({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full px-3 py-1.5 text-[13px] font-medium",
      dark ? "bg-brand/80 text-[#f3efe6]" : "bg-[#f3efe6] text-brand/80",
    )}
  >
    {children}
  </span>
);

const OptionButton = ({
  label,
  selected,
  onClick,
  compact = false,
  center = false,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  compact?: boolean;
  center?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={cn(
      "w-full rounded-[14px] border font-medium transition-colors",
      compact ? "px-3 py-3 text-[15px]" : "px-[18px] py-4 text-[15px] md:text-[16px]",
      center ? "text-center" : "text-left",
      selected
        ? "border-brand/80 bg-brand/80 text-[#f3efe6]"
        : "border-brand/20 bg-white text-brand/80 hover:border-brand/40",
    )}
    style={{ wordBreak: "keep-all" }}
  >
    {label}
  </button>
);

const PrimaryButton = ({
  children,
  disabled,
  onClick,
  className,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "h-[54px] w-full rounded-[14px] text-[16px] font-bold transition-colors",
      disabled ? "cursor-not-allowed bg-brand/20 text-[#f3efe6]" : "bg-brand/80 text-[#f3efe6] hover:bg-brand/90",
      className,
    )}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, onClick, className }: { children: React.ReactNode; onClick?: () => void; className?: string }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "h-[54px] w-full rounded-[14px] border border-brand/80 bg-white text-[16px] font-bold text-brand/80 transition-colors hover:bg-[#f3efe6]",
      className,
    )}
  >
    {children}
  </button>
);

const Card = ({ children, className, soft = false }: { children: React.ReactNode; className?: string; soft?: boolean }) => (
  <div
    className={cn(
      "rounded-[18px] p-5 md:p-7",
      soft ? "bg-[#f3efe6]" : "border border-brand/15 bg-white",
      className,
    )}
  >
    {children}
  </div>
);

/** 8성향 레이더 ("입맛 지문"). 값은 0~100 */

// ---------------------------------------------------------------------------
// 페이지
// ---------------------------------------------------------------------------
const Quiz = () => {
  const [phase, setPhase] = useState<Phase>("intro");
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState<Selections>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [sharedTrait, setSharedTrait] = useState<Trait | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [retakeOf, setRetakeOf] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<"none" | "accurate" | "picking" | "sent">("none");
  const [picked, setPicked] = useState<Trait | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  /*
   * 로그인한 사람만 들어온다. 아니면 앱 받는 안내로 보낸다.
   *
   * 이 화면은 두 길로 들어온다 — 환경설정에서, 그리고 남이 보낸 공유 링크로.
   * 공유 링크를 받은 사람은 앱이 없거나 가입 전일 수 있다. 라우트에
   * ProtectedRoute 를 씌우면 그 사람이 로그인 화면으로 떨어지는데, 계정이
   * 없는 사람에게는 막다른 길이다. 그래서 여기서 직접 갈라 보낸다.
   */
  const { initAuth, isAuthenticated } = useAuthStore();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    initAuth();
    setAuthChecked(true);
  }, [initAuth]);

  useEffect(() => {
    if (authChecked && !isAuthenticated) {
      window.location.replace("/download.html");
    }
  }, [authChecked, isAuthenticated]);

  // 공유 링크(/r/{trait}.html → /quiz?result={trait})로 들어오면 그 유형 소개를 바로 보여 준다.
  // 옛 4글자 코드(?result=ESLP)는 처음 화면으로 보낸다.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("result");
    if (isTrait(code)) {
      setSharedTrait(code);
      setPhase("shared");
    }
  }, []);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, [phase, step]);

  const question = step < QUESTIONS.length ? QUESTIONS[step] : null;

  const start = () => {
    if (!consent) return;
    setSel({});
    setStep(0);
    setResult(null);
    setFeedback("none");
    setPicked(null);
    setPhase("playing");
    logEvent("start");
  };

  const restart = () => {
    setRetakeOf(responseId);
    setResponseId(null);
    window.history.replaceState(null, "", "/taste-quiz");
    setPhase("intro");
  };

  // 답을 고르면 잠깐 선택 표시를 보여 준 뒤 바로 다음으로 넘어간다 (다음 버튼 없음).
  // 보조 질문이 있는 문항은 보조 답까지 골라야 넘어간다.
  const advancing = useRef(false);

  const advance = async (s: Selections) => {
    if (question) {
      logEvent("answer", question.id);
      setStep((x) => x + 1);
      return;
    }
    logEvent("answer", CHIP.id);
    const r = score(s);
    setResult(r);
    setPhase("result");
    logEvent("complete");
    setResponseId(await submitResponse(s, r, retakeOf));
  };

  const choose = (id: string, answer: Answer) => {
    if (advancing.current) return;
    const nextSel = { ...sel, [id]: answer.id };
    // 보조 질문이 있는 문항에서 메인 답을 바꾸면 보조 답을 다시 고르게 한다
    if (question?.followUp && id === question.id && sel[id] !== answer.id) delete nextSel[question.followUp.id];
    setSel(nextSel);

    const done = question
      ? Boolean(nextSel[question.id]) && (!question.followUp || Boolean(nextSel[question.followUp.id]))
      : Boolean(nextSel[CHIP.id]);
    if (!done) return;

    advancing.current = true;
    window.setTimeout(() => {
      advancing.current = false;
      void advance(nextSel);
    }, 250);
  };

  const back = () => {
    if (step === 0) setPhase("intro");
    else setStep((s) => s - 1);
  };

  const share = async (trait: Trait) => {
    logEvent("share_click");
    const t = TYPES[trait];
    /*
     * 공유 링크는 앱의 취향분석 화면을 가리킨다.
     *
     * 예전에는 /r/{trait}.html 이었는데 그 파일은 웹사이트(tastee.kr)에만
     * 있다. 앱에서 공유하면 app.tastee.kr/r/... 가 되어 SPA 폴백(빈 화면)으로
     * 떨어졌다. 받은 사람이 앱이 없거나 로그인 전이면 아래 문지기가
     * download.html 로 보낸다.
     */
    const url = `${window.location.origin}/taste-quiz?result=${trait}`;
    const text = `나의 유형은 "${t.name}" | TASTEE 취향 분석`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "TASTEE", text, url });
      } catch {
        /* 사용자가 닫음 */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toastUtil.success("링크가 복사되었습니다");
    } catch {
      toastUtil.error("공유에 실패했습니다");
    }
  };


  const giveFeedback = async (kind: "accurate" | "inaccurate", trait?: Trait) => {
    logEvent("feedback");
    setFeedback(kind === "accurate" ? "accurate" : "sent");
    await sendFeedback(responseId, kind, trait);
  };

  // 로그인 확인 전/비로그인은 아무것도 그리지 않는다 (위에서 보내진다)
  if (!authChecked || !isAuthenticated) return null;

  // -------------------------------------------------------------------------
  return (
    <div className="flex h-full flex-col bg-backgroundColorBg2 text-brand/80">
      <AppBar title="취향 분석" isBottomBorder />
      <main className="flex-1 overflow-y-auto scrollbar-hide pb-24 pt-6">
        <div ref={topRef} className="scroll-mt-32" />

        {/* ------------------------------ 시작 (M1 / D1) ------------------------------ */}
        {phase === "intro" && (
          <div className="container mx-auto max-w-[1120px] px-6 animate-fade-in">
            <div className="grid items-center gap-12 lg:grid-cols-[1fr_480px] lg:gap-20 lg:py-10">
              <div className="flex flex-col gap-6">
                {/* 제목은 AppBar 의 '취향분석' 이 대신한다 — 두 번 적지 않는다 */}
                <p className="text-[16px] leading-relaxed text-brand/60 md:text-[18px]">
                  {QUESTIONS.length}문항, 2분이면 나의 취향이 나와요.
                  <span className="hidden md:inline">
                    <br />8가지 성향 중 무엇이 가장 강한지, 숨은 성향까지 알려 드려요.
                  </span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {TRAITS.map((t) => (
                    <Pill key={t}>{TRAIT_LABEL[t]}</Pill>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- 문항 / 칩 (M2~M4 / D2) ------------------------- */}
        {phase === "playing" && (
          <div className="container mx-auto max-w-[640px] px-6 animate-fade-in">
            <div className="flex items-center gap-3 md:gap-4">
              <button type="button" onClick={back} className="shrink-0 text-[20px] font-medium text-brand/80 md:text-[14px] md:text-brand/55" aria-label="이전">
                <span className="md:hidden">←</span>
                <span className="hidden md:inline">← 이전</span>
              </button>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-brand/15">
                <div className="h-full rounded-full bg-brand/80 transition-all duration-300" style={{ width: `${((step + 1) / STEPS) * 100}%` }} />
              </div>
              <span className="shrink-0 text-[13px] font-medium text-brand/55 md:text-[14px]">
                {Math.min(step + 1, QUESTIONS.length)} / {QUESTIONS.length}
              </span>
            </div>

            {!question && (
              <div className="mt-6 flex items-center">
              </div>
            )}

            {question ? (
              <>
                <p className="mt-8 text-[13px] font-bold text-brand/55 md:text-[14px]">Q{step + 1}</p>
                <h2 className="mt-1.5 text-[22px] font-bold leading-[1.35] text-brand/85 md:text-[32px] md:leading-[1.3]" style={{ wordBreak: "keep-all" }}>
                  {question.question}
                </h2>
                <div className="mt-6 flex flex-col gap-2.5">
                  {question.answers.map((a) => (
                    <OptionButton key={a.id} label={a.label} selected={sel[question.id] === a.id} onClick={() => choose(question.id, a)} />
                  ))}
                </div>
                {question.followUp && sel[question.id] && (
                  <div className="mt-5 rounded-2xl bg-[#f3efe6] p-4 animate-fade-in md:p-5">
                    <p className="text-[15px] font-bold text-brand/85 md:text-[16px]" style={{ wordBreak: "keep-all" }}>
                      {question.followUp.question}
                    </p>
                    <div className={cn("mt-3 grid gap-2", question.followUp.answers.length === 3 ? "grid-cols-3" : "grid-cols-2")}>
                      {question.followUp.answers.map((a) => (
                        <OptionButton
                          key={a.id}
                          compact
                          center
                          label={a.label}
                          selected={sel[question.followUp!.id] === a.id}
                          onClick={() => choose(question.followUp!.id, a)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="mt-4 text-[22px] font-bold leading-[1.35] text-brand/85 md:text-[32px]" style={{ wordBreak: "keep-all" }}>
                  {CHIP.question}
                </h2>
                <div className="mt-6 grid grid-cols-2 gap-2.5">
                  {CHIP.answers.map((a) => (
                    <OptionButton key={a.id} label={a.label} selected={sel[CHIP.id] === a.id} onClick={() => choose(CHIP.id, a)} />
                  ))}
                </div>
              </>
            )}

          </div>
        )}

        {/* ------------------------------ 결과 (M5 / D3) ------------------------------ */}
        {phase === "result" && result && (
          <ResultView
            result={result}
            onShare={() => share(result.primary)}
            onRestart={restart}
            feedback={feedback}
            onAccurate={() => giveFeedback("accurate")}
            onInaccurate={() => setFeedback("picking")}
          />
        )}

        {/* -------------------- 공유 링크로 들어온 유형 소개 -------------------- */}
        {phase === "shared" && sharedTrait && (
          <div className="container mx-auto max-w-[640px] px-6 animate-fade-in">
            <TypeHeader trait={sharedTrait} />
            <Card className="mt-6">
              <TypeBody trait={sharedTrait} />
            </Card>
            <div className="mt-6 flex flex-col gap-2.5">
              <PrimaryButton
                onClick={() => {
                  window.history.replaceState(null, "", "/taste-quiz");
                  setPhase("intro");
                }}
              >
                나도 테스트 해보기
              </PrimaryButton>
            </div>
          </div>
        )}
      </main>

      {/*
       * 시작 버튼은 화면 아래 고정이다.
       *
       * 웹사이트에서는 본문 흐름 안에 있었는데, 앱에서는 다른 화면들이
       * (리뷰 키워드 선택 등) 전부 아래에 둔다. 본문 안에 있으면 같은 앱인데
       * 이 화면만 버튼이 중간에 떠 있다.
       */}
      {phase === "intro" && (
        <div className="px-20px pb-20px pt-8px">
          {/*
           * 동의는 버튼 **바로 위**에 둔다. 본문 위쪽에 있으면 화면이 길 때
           * 버튼까지 내려온 사람이 무엇에 동의하고 누르는지 못 본다.
           */}
          <label className="mb-12px flex cursor-pointer items-start gap-2.5 text-[13px] leading-relaxed text-brand/60">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded-[5px] accent-[rgb(75,54,33)]"
            />
            응답은 서비스 개선과 맞춤 추천에 활용됩니다.
          </label>
          {/*
           * 앱 공용 SubmitButton 을 쓴다. 옮겨온 PrimaryButton 은
           * rounded-[14px] 라 앱의 다른 버튼(rounded-full)과 모서리가 달랐다.
           */}
          <SubmitButton text="테스트 시작하기" disabled={!consent} onSubmit={start} />
        </div>
      )}

      {/* 결과가 다를 때: 가까운 유형 고르기 (M6) */}
      {feedback === "picking" && result && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:items-center" onClick={() => setFeedback("none")}>
          <div
            className="max-h-[90vh] w-full overflow-y-auto rounded-t-3xl bg-surface p-6 pb-8 md:max-w-[480px] md:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <p className="text-[18px] font-bold text-brand/85">나는 사실 이쪽에 더 가까워요</p>
            <p className="mt-1 text-[13px] text-brand/55">하나만 골라 주세요. 다음 테스트를 더 정확하게 만드는 데 써요.</p>
            <div className="mt-4 flex flex-col gap-2">
              {TRAITS.filter((t) => t !== result.primary).map((t) => (
                <OptionButton key={t} compact label={TYPES[t].name} selected={picked === t} onClick={() => setPicked(t)} />
              ))}
            </div>
            <PrimaryButton className="mt-4" disabled={!picked} onClick={() => picked && giveFeedback("inaccurate", picked)}>
              보내기
            </PrimaryButton>
          </div>
        </div>
      )}

    </div>
  );
};

// ---------------------------------------------------------------------------
// 결과 조각
// ---------------------------------------------------------------------------
const TypeHeader = ({ trait, second }: { trait: Trait; second?: Trait }) => {
  const t = TYPES[trait];
  return (
    <div className="flex flex-col items-start gap-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Pill>나의 유형</Pill>
        {second && <Pill dark>+ {SECOND_LINE[second]}</Pill>}
      </div>
      {t.image && <img src={t.image} alt="" className="my-2 w-full max-w-[360px] self-center" />}
      {/*
        * keep-all 로 낱말 안에서는 안 끊고, text-wrap: balance 로 줄 길이를
        * 고르게 나눈다. 이게 없으면 긴 이름이 '…웨이팅 / 장인' 처럼 마지막
        * 줄에 한 낱말만 남아 읽기 나쁘다.
        */}
      <h1
        className="text-[26px] font-bold leading-[1.32] text-brand/85 md:text-[40px] md:leading-[1.28]"
        style={{ wordBreak: "keep-all", textWrap: "balance" }}
      >
        {t.name}
      </h1>
      {/* 한마디와 숨은 성향을 한 줄에. 좁으면 배지가 다음 줄로 내려간다 */}
      <p className="text-[16px] font-medium text-brand/55 md:text-[18px]">“{t.oneLiner}”</p>
    </div>
  );
};

const TypeBody = ({ trait }: { trait: Trait }) => {
  const t = TYPES[trait];
  return (
    <>
      <p className="text-[15px] leading-[1.7] text-brand/80 md:text-[17px]" style={{ wordBreak: "keep-all" }}>
        {t.description}
      </p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {t.tags.map((tag) => (
          <Pill key={tag}>{tag}</Pill>
        ))}
      </div>
      <p className="mt-3 text-[13px] font-medium text-brand/55 md:text-[14px]">대표 식당: {t.restaurant}</p>
    </>
  );
};

const ResultView = ({
  result,
  onShare,
  onRestart,
  feedback,
  onAccurate,
  onInaccurate,
}: {
  result: QuizResult;
  onShare: () => void;
  onRestart: () => void;
  feedback: "none" | "accurate" | "picking" | "sent";
  onAccurate: () => void;
  onInaccurate: () => void;
}) => {
  const { primary, secondary } = result;
  const combo = findCombo(primary, secondary);
  return (
    <div className="container mx-auto max-w-[1120px] px-6 animate-fade-in">
      <div className="grid gap-5 lg:grid-cols-[500px_1fr] lg:gap-12">
        {/* 왼쪽: 유형 + 입맛 지문 */}
        <div className="flex flex-col gap-5">
          <TypeHeader trait={primary} second={secondary} />
        </div>

        {/* 오른쪽: 설명 + 행동 + 피드백 */}
        <div className="flex flex-col gap-5">
          <Card>
            <p className="mb-3 hidden text-[14px] font-bold text-brand/55 lg:block">유형 설명</p>
            <TypeBody trait={primary} />
          </Card>

          <Card soft>
            {combo ? (
              <>
                <p className="text-[13px] font-medium text-brand/55 md:text-[14px]">그중에서도 당신은</p>
                <p className="mt-1 text-[18px] font-bold leading-[1.4] text-brand/85 md:text-[22px]" style={{ wordBreak: "keep-all" }}>
                  {combo.name}
                </p>
                <p className="mt-3 text-[15px] leading-[1.7] text-brand/80 md:text-[17px]" style={{ wordBreak: "keep-all" }}>
                  {combo.description}
                </p>
              </>
            ) : (
              <>
                <p className="text-[13px] font-medium text-brand/55 md:text-[14px]">숨은 성향: {TRAIT_LABEL[secondary]}</p>
                <p className="mt-2 text-[15px] leading-[1.7] text-brand/80 md:text-[17px]" style={{ wordBreak: "keep-all" }}>
                  {SECOND_PARAGRAPH[secondary]}
                </p>
              </>
            )}
          </Card>

          <SecondaryButton onClick={onShare}>결과 공유하기</SecondaryButton>

          <Card className="flex flex-col items-center gap-3">
            {feedback === "accurate" || feedback === "sent" ? (
              <p className="py-2 text-center text-[15px] font-medium text-brand/80">고마워요! 더 정확한 테스트를 만드는 데 쓸게요.</p>
            ) : (
              <>
                <p className="text-[15px] font-bold text-brand/85 md:text-[16px]">이 결과, 나랑 맞아요?</p>
                <div className="grid w-full grid-cols-2 gap-2.5">
                  <OptionButton compact center label="정확해요" selected={false} onClick={onAccurate} />
                  <OptionButton compact center label="별로 정확하지 않아요" selected={false} onClick={onInaccurate} />
                </div>
              </>
            )}
            <button type="button" onClick={onRestart} className="text-[14px] font-medium text-brand/55 underline-offset-4 hover:underline">
              다시 하기
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
